import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUserProgress } from '../../config/api';
import { FactProgress } from '../../types/progress';
import { TRACK_RANGES } from '../../types/constants';
import { PreKPracticeSettings } from './types';
import NumberNamingPractice from './NumberNaming/NumberNamingPractice';
import CountingPractice from './Counting/CountingPractice';
import SubitizingPractice from './Subitizing/SubitizingPractice';
import WhatComesNextPractice from './WhatComesNext/WhatComesNextPractice';
import PracticeStartScreen from './PracticeStartScreen';
import { motion } from 'framer-motion';
import UserProfileDropdown from '../Dashboard/UserProfileDropdown';

const PreKPracticePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [factsToPractice, setFactsToPractice] = useState<string[]>([]);
  const [currentPracticeType, setCurrentPracticeType] = useState<'learning' | 'accuracy' | null>(null);
  const [practiceStarted, setPracticeStarted] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<'TRACK_PREK_NAMING' | 'TRACK_PREK_SUBITIZING' | 'TRACK_PREK_COUNTING' | 'TRACK_PREK_WHATCOMESNEXT' | null>(null);
  const [settings, setSettings] = useState<PreKPracticeSettings>({
    trackId: 'TRACK_PREK_NAMING',
    speechEnabled: true
  });

  // Convert fact ID to digit (FACT1574 = 0, FACT1575 = 1, etc.)
  const factToDigit = (factId: string): number => {
    const factNumber = parseInt(factId.replace('FACT', ''));
    return factNumber - 1574;
  };

  // Convert fact ID to quantity for subitizing (FACT1584 = 1, FACT1585 = 2, etc.)
  const factToQuantity = (factId: string): number => {
    const factNumber = parseInt(factId.replace('FACT', ''));
    return factNumber - 1583;
  };

  useEffect(() => {
    const fetchAndFilterFacts = async () => {
      if (!user?.userId) return;

      try {
        setIsLoading(true);

        // Determine which track to use based on URL or progress
        // First check for URL parameter or stored preference
        const urlParams = new URLSearchParams(window.location.search);
        const trackParam = urlParams.get('track');

        let targetTrack: 'TRACK_PREK_NAMING' | 'TRACK_PREK_SUBITIZING' | 'TRACK_PREK_COUNTING' | 'TRACK_PREK_WHATCOMESNEXT';

        if (trackParam === 'subitizing') {
          targetTrack = 'TRACK_PREK_SUBITIZING';
        } else if (trackParam === 'naming') {
          targetTrack = 'TRACK_PREK_NAMING';
        } else if (trackParam === 'counting') {
          targetTrack = 'TRACK_PREK_COUNTING';
        } else if (trackParam === 'whatcomesnext') {
          targetTrack = 'TRACK_PREK_WHATCOMESNEXT';
        } else {
          // Default behavior - check both tracks and prioritize based on needs
          const progressData = await getUserProgress(user.userId, 'TRACK_PREK_NAMING');
          const namingTrack = progressData.tracks.find(track => track.trackId === 'TRACK_PREK_NAMING');

          // Check if naming track has facts that need practice
          let hasNamingPracticeNeeds = false;
          if (namingTrack) {
            const [startFactId, endFactId] = TRACK_RANGES['TRACK_PREK_NAMING'];
            for (let factNum = startFactId; factNum <= endFactId; factNum++) {
              const factId = `FACT${factNum}`;
              const factProgress = namingTrack.facts[factId];
              if (!factProgress || factProgress.status === 'learning' || factProgress.status === 'accuracyPractice' || factProgress.status === 'fluency15Practice' || factProgress.status === 'fluency6Practice') {
                hasNamingPracticeNeeds = true;
                break;
              }
            }
          }

          if (hasNamingPracticeNeeds) {
            targetTrack = 'TRACK_PREK_NAMING';
          } else {
            // Check subitizing track
            const subitizingProgressData = await getUserProgress(user.userId, 'TRACK_PREK_SUBITIZING');
            const subitizingTrack = subitizingProgressData.tracks.find(track => track.trackId === 'TRACK_PREK_SUBITIZING');

            if (subitizingTrack) {
              targetTrack = 'TRACK_PREK_SUBITIZING';
            } else {
              targetTrack = 'TRACK_PREK_NAMING'; // Default fallback
            }
          }
        }

        // Fetch progress for the determined track
        const progressData = await getUserProgress(user.userId, targetTrack);
        const preKTrack = progressData.tracks.find(track => track.trackId === targetTrack);

        if (!preKTrack) {
          // Track not found, redirect to assessment (same pattern as other cases)
          navigate('/pre-k-assessment');
          return;
        }

        setCurrentTrack(targetTrack);
        setSettings(prev => ({ ...prev, trackId: targetTrack }));
        sessionStorage.setItem('activeTrackId', targetTrack);

        // Helper function to categorize a fact based on its progress
        const categorizeFact = (
          factId: string,
          factProgress: FactProgress | undefined,
          factsToLearn: string[],
          factsForAccuracyPractice: string[]
        ) => {
          if (!factProgress || factProgress.status === 'learning') {
            factsToLearn.push(factId);
          } else if (factProgress.status === 'accuracyPractice') {
            // For accuracy practice status, check if low accuracy with no today stats
            const lowAccuracy = factProgress.accuracyRate < 0.8;
            const hasNoTodayStats = !factProgress.todayStats || Object.keys(factProgress.todayStats).length === 0;
            if (lowAccuracy && hasNoTodayStats) {
              factsToLearn.push(factId);
            } else {
              factsForAccuracyPractice.push(factId);
            }
          }
          // fluency15Practice, fluency6Practice, mastered, and other statuses don't need practice
        };

        // Categorize facts based on status and progress
        const factsToLearn: string[] = [];
        const factsForAccuracyPractice: string[] = [];

        // Collect all track facts for passing to accuracy/fluency15 screens
        const allTrackFacts: string[] = [];

        if (targetTrack === 'TRACK_PREK_NAMING') {
          // Number naming facts: digits 0-9
          const [startFactId] = TRACK_RANGES['TRACK_PREK_NAMING'];
          for (let digit = 0; digit <= 9; digit++) {
            const factId = `FACT${startFactId + digit}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = preKTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice);
          }
        } else if (targetTrack === 'TRACK_PREK_SUBITIZING') {
          // Subitizing facts: quantities 1-4
          const [startFactId] = TRACK_RANGES['TRACK_PREK_SUBITIZING'];
          for (let quantity = 1; quantity <= 4; quantity++) {
            const factId = `FACT${startFactId + (quantity - 1)}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = preKTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice);
          }
        } else if (targetTrack === 'TRACK_PREK_COUNTING') {
          // Counting facts: quantities 1-5
          const [startFactId] = TRACK_RANGES['TRACK_PREK_COUNTING'];
          for (let quantity = 1; quantity <= 5; quantity++) {
            const factId = `FACT${startFactId + (quantity - 1)}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = preKTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice);
          }
        } else if (targetTrack === 'TRACK_PREK_WHATCOMESNEXT') {
          // What Comes Next facts: targets 3-9 map to FACT1595-FACT1601
          // FACT1593=target1, FACT1594=target2 (unused), FACT1595=target3, etc.
          const [startFactId] = TRACK_RANGES['TRACK_PREK_WHATCOMESNEXT'];
          for (let target = 3; target <= 9; target++) {
            const factId = `FACT${startFactId + (target - 1)}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = preKTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice);
          }
        }

        // Determine which practice type to use
        // Priority order: learning → accuracy → no practice needed → assessment
        if (factsToLearn.length >= 1) {
          setFactsToPractice(factsToLearn);
          setCurrentPracticeType('learning');
        } else if (factsForAccuracyPractice.length >= 1) {
          // For accuracy practice, pass ALL track facts (not just those in accuracyPractice status)
          setFactsToPractice(allTrackFacts);
          setCurrentPracticeType('accuracy');
        } else {
          // No practice needed, redirect to assessment
          navigate('/pre-k-assessment');
          return;
        }
      } catch (error: any) {
        console.error('Error fetching progress data:', error);
        // If no progress exists (404) or any data loading issue, redirect to assessment
        // so the student can start fresh rather than seeing a dead-end error screen
        if (error?.response?.status === 404 || error?.message?.includes('No progress')) {
          navigate('/pre-k-assessment');
          return;
        }
        setError('Failed to load practice data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndFilterFacts();
  }, [user?.userId]);

  const handlePracticeComplete = () => {
    // Re-trigger fact evaluation to determine next steps
    window.location.reload();
  };

  const handleBack = () => {
    // Use full page reload to ensure fresh user data (including updated focusTrack)
    window.location.href = '/';
  };

  // Convert fact IDs to format expected by practice screens
  const getFactsWithDigits = (factIds: string[]) => {
    if (currentTrack === 'TRACK_PREK_SUBITIZING') {
      const [startFactId] = TRACK_RANGES['TRACK_PREK_SUBITIZING'];
      return factIds.map(factId => {
        const factNumber = parseInt(factId.replace('FACT', ''));
        const quantity = factNumber - startFactId + 1; // FACT1584 = 1, etc.
        return { factId, digit: quantity }; // Using 'digit' field for compatibility but containing quantity
      });
    } else if (currentTrack === 'TRACK_PREK_COUNTING') {
      const [startFactId] = TRACK_RANGES['TRACK_PREK_COUNTING'];
      return factIds.map(factId => {
        const factNumber = parseInt(factId.replace('FACT', ''));
        const quantity = factNumber - startFactId + 1; // FACT1588 = 1, etc.
        return { factId, digit: quantity }; // Using 'digit' field for compatibility but containing quantity
      });
    } else if (currentTrack === 'TRACK_PREK_WHATCOMESNEXT') {
      const [startFactId] = TRACK_RANGES['TRACK_PREK_WHATCOMESNEXT'];
      return factIds.map(factId => {
        const factNumber = parseInt(factId.replace('FACT', ''));
        const target = factNumber - startFactId + 1; // FACT1595 = 3, FACT1596 = 4, etc.
        return { factId, digit: target }; // Using 'digit' field for compatibility but containing target number
      });
    } else {
      // TRACK_PREK_NAMING: digits 0-9
      const [startFactId] = TRACK_RANGES['TRACK_PREK_NAMING'];
      return factIds.map(factId => {
        const factNumber = parseInt(factId.replace('FACT', ''));
        const digit = factNumber - startFactId; // FACT1574 = 0, etc.
        return { factId, digit };
      });
    }
  };

  const handlePracticeStart = () => {
    setPracticeStarted(true);
  };

  const getPracticeTitle = (): string => {
    let trackName = 'Numbers';
    switch (currentTrack) {
      case 'TRACK_PREK_NAMING':
        trackName = 'Numbers';
        break;
      case 'TRACK_PREK_SUBITIZING':
        trackName = 'Dot Counting';
        break;
      case 'TRACK_PREK_COUNTING':
        trackName = 'Counting';
        break;
      case 'TRACK_PREK_WHATCOMESNEXT':
        trackName = 'What Comes Next';
        break;
    }

    switch (currentPracticeType) {
      case 'learning':
        return `Let's Practice ${trackName}!`;
      case 'accuracy':
        return `Let's Practice ${trackName} Accuracy!`;
      default:
        return `Let's Practice ${trackName}!`;
    }
  };

  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-8 rounded-2xl shadow-lg text-center"
        >
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading your practice session...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md"
        >
          <div className="text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Practice Session</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleBack}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Back to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  // Show start screen first, then practice screens
  if (!practiceStarted) {
    return (
      <div className="relative">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <PracticeStartScreen
          title={getPracticeTitle()}
          onStart={handlePracticeStart}
          currentTrack={currentTrack}
        />
      </div>
    );
  }

  // Render appropriate practice component based on practice type and track
  if (currentPracticeType === 'learning') {
    if (currentTrack === 'TRACK_PREK_SUBITIZING') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <SubitizingPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType="learning"
          />
        </div>
      );
    } else if (currentTrack === 'TRACK_PREK_NAMING') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <NumberNamingPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType="learning"
          />
        </div>
      );
    } else if (currentTrack === 'TRACK_PREK_COUNTING') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <CountingPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType="learning"
          />
        </div>
      );
    } else if (currentTrack === 'TRACK_PREK_WHATCOMESNEXT') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <WhatComesNextPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType="learning"
          />
        </div>
      );
    } else {
      // Unknown track, redirect back to assessment
      navigate('/pre-k-assessment');
      return null;
    }
  } else if (currentPracticeType === 'accuracy') {
    if (currentTrack === 'TRACK_PREK_SUBITIZING') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <SubitizingPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType={currentPracticeType}
          />
        </div>
      );
    } else if (currentTrack === 'TRACK_PREK_NAMING') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <NumberNamingPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType={currentPracticeType}
          />
        </div>
      );
    } else if (currentTrack === 'TRACK_PREK_COUNTING') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <CountingPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType={currentPracticeType}
          />
        </div>
      );
    } else if (currentTrack === 'TRACK_PREK_WHATCOMESNEXT') {
      return (
        <div className="relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 z-50">
              <UserProfileDropdown />
            </div>
          )}
          <WhatComesNextPractice
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={handlePracticeComplete}
            onBack={handleBack}
            initialPracticeType={currentPracticeType}
          />
        </div>
      );
    } else {
      // Unknown track, redirect back to assessment
      navigate('/pre-k-assessment');
      return null;
    }
  }

  return null;
};

export default PreKPracticePage;